import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';

const STAGE_LABELS = {
  target: 'Target', contact_attempted: 'Contact Attempted', decision_maker_identified: 'DM Identified',
  introduction_made: 'Introduction Made', capability_package_sent: 'Cap Package Sent', vendor_process: 'Vendor Process',
  approved_vendor: 'Approved Vendor', bid_list: 'Bid List', bid_invitation_received: 'Bid Invitation',
  first_bid_submitted: 'First Bid', first_job_won: 'First Job Won', active_account: 'Active',
  repeat_account: 'Repeat Account', dormant: 'Dormant', lost_account: 'Lost',
};

const STAGE_COLORS = {
  target: '#94a3b8', contact_attempted: '#6366f1', decision_maker_identified: '#8b5cf6',
  introduction_made: '#a855f7', capability_package_sent: '#d946ef', vendor_process: '#ec4899',
  approved_vendor: '#f43f5e', bid_list: '#f97316', bid_invitation_received: '#eab308',
  first_bid_submitted: '#84cc16', first_job_won: '#22c55e', active_account: '#16a34a',
  repeat_account: '#059669', dormant: '#78716c', lost_account: '#dc2626',
};

const PRIORITY_COLORS = { A: '#dc2626', B: '#f59e0b', C: '#94a3b8' };

export default function CommercialDashboard({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/dashboard`, authOpts());
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Loading commercial dashboard...</div>;

  const sb = data.scoreboard || {};
  const sc = data.stage_counts || {};
  const pc = data.priority_counts || {};
  const goalPct = sb.repeat_goal ? Math.min((sb.repeat_accounts / sb.repeat_goal) * 100, 100) : 0;

  return (
    <div data-testid="commercial-dashboard" style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '24px 28px' }}>
      {/* Scoreboard Hero */}
      <div data-testid="repeat-account-scoreboard" style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 12, padding: isMobile ? '16px 14px' : '28px 32px',
        marginBottom: 20, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : '1fr 1fr 1fr 1fr', gap: isMobile ? 12 : 24, color: '#fff',
      }}>
        <div style={{ gridColumn: isMobile ? 'span 2' : 'span 1' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Repeat Contractor Accounts</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 48, fontWeight: 900, color: sb.repeat_accounts > 0 ? '#22c55e' : '#fff' }}>{sb.repeat_accounts}</span>
            <span style={{ fontSize: 22, fontWeight: 600, color: '#64748b' }}>/ {sb.repeat_goal}</span>
          </div>
          <div style={{ marginTop: 8, height: 6, background: '#475569', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${goalPct}%`, background: goalPct >= 100 ? '#22c55e' : '#c0392b', borderRadius: 3, transition: 'width .5s' }} />
          </div>
        </div>
        <ScoreCard label="First Jobs Won" value={sb.first_jobs_won} color="#eab308" />
        <ScoreCard label="Active Accounts" value={sb.active_accounts} color="#3b82f6" />
        <ScoreCard label="Total Accounts" value={data.total_accounts} color="#fff" />
      </div>

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        <KPICard label="A-Priority" value={pc.A || 0} color="#dc2626" />
        <KPICard label="Needs Contact" value={data.needs_contact} color="#f59e0b" />
        <KPICard label="Decision Makers" value={data.decision_makers} color="#8b5cf6" />
        <KPICard label="Bids Due This Week" value={data.bids_due_this_week} color="#ef4444" />
        <KPICard label="Open Bids" value={data.open_bids} color="#f97316" />
      </div>

      {/* Revenue Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
        <RevenueCard label="Commercial Pipeline" value={money(data.pipeline_value)} color="#3b82f6" />
        <RevenueCard label="Commercial Quoted" value={money(data.quoted_value)} color="#f59e0b" />
        <RevenueCard label="Commercial Won Revenue" value={money(data.won_revenue)} color="#16a34a" />
      </div>

      {/* Account Pipeline */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 14px' }}>Account Development Pipeline</h3>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Object.entries(STAGE_LABELS).map(([key, label]) => {
            const count = sc[key] || 0;
            const color = STAGE_COLORS[key];
            return (
              <div key={key} data-testid={`stage-${key}`} style={{
                padding: '8px 12px', borderRadius: 6, background: `${color}10`, border: `1px solid ${color}30`,
                textAlign: 'center', minWidth: 72, flex: '1 1 auto',
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color }}>{count}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: '#64748b', lineHeight: 1.2 }}>{label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Priority Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Priority Distribution</h3>
          {['A', 'B', 'C'].map(p => (
            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: PRIORITY_COLORS[p], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 800 }}>{p}</div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: PRIORITY_COLORS[p], borderRadius: 3, width: `${data.total_accounts ? ((pc[p] || 0) / data.total_accounts * 100) : 0}%` }} />
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', minWidth: 24, textAlign: 'right' }}>{pc[p] || 0}</span>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>{p === 'A' ? 'Dream / High-Value' : p === 'B' ? 'Strong Potential' : 'Prospect'}</span>
            </div>
          ))}
        </div>
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Key Metrics</h3>
          <MetricRow label="Target Accounts" value={sc.target || 0} />
          <MetricRow label="Approved Vendors" value={sc.approved_vendor || 0} />
          <MetricRow label="On Bid Lists" value={sc.bid_list || 0} />
          <MetricRow label="Bids Submitted" value={sc.first_bid_submitted || 0} />
          <MetricRow label="Dormant" value={sc.dormant || 0} warn />
          <MetricRow label="Lost" value={sc.lost_account || 0} warn />
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

function KPICard({ label, value, color }) {
  return (
    <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{value}</div>
    </div>
  );
}

function RevenueCard({ label, value, color }) {
  return (
    <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function MetricRow({ label, value, warn }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: warn && value > 0 ? '#dc2626' : '#1e293b' }}>{value}</span>
    </div>
  );
}

export { STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS };
